import React, { Component } from 'react';
import Helmet from 'react-helmet';
import { graphql } from 'gatsby';
import MDXRenderer from 'gatsby-plugin-mdx/mdx-renderer';

import { Layout, Link } from '$components';
import NextPrevious from '../components/NextPrevious';
import config from '../../config';
import { Edit, StyledHeading, StyledMainWrapper } from '../components/styles/Docs';
import { LocaleProvider } from '../i18n/LocaleContext';
import { locales, defaultLocale } from '../i18n/locales';

const forcedNavOrder = config.sidebar.forcedNavOrder;

export default class MDXRuntimeTest extends Component {
  render() {
    const { data, pageContext } = this.props;
    const locale = (pageContext && pageContext.locale) || defaultLocale;
    const localeConfig = locales[locale] || locales[defaultLocale];
    const localeSlug = localeConfig.slug;
    const isDefault = localeConfig.isDefault;

    if (!data || !data.site || !data.mdx) {
      // This branch renders /docs/404 (and any route with no matching mdx).
      // It returns before the Helmet block below, so the page shipped an empty
      // <title> and no robots tag while still responding 200 - a soft 404.
      // Ported from lfdocs d4289f3, where Search Console had indexed the
      // equivalent page on 2,515 impressions. Genuinely missing paths already
      // return a real 404 via nginx; only this built /docs/404 route responds 200.
      return (
        <LocaleProvider locale={locale}>
          <Helmet>
            <title>Page not found | DesignerBox Docs</title>
            <meta name="robots" content="noindex,follow" />
          </Helmet>
          <div>Content not found</div>
        </LocaleProvider>
      );
    }
    const {
      allMdx,
      mdx,
      site: {
        siteMetadata: { docsLocation, title },
      },
    } = data;

    const gitHub = require('../components/images/github.svg');

    // Prefix forcedNavOrder entries for non-default locales
    const localeForcedNavOrder = isDefault
      ? forcedNavOrder
      : forcedNavOrder.map(p => `/${localeSlug}${p}`);

    const navItems = allMdx.edges
      .map(({ node }) => node.fields.slug)
      .filter(slug => slug !== '/' && slug !== `/${localeSlug}`)
      .sort()
      .reduce(
        (acc, cur) => {
          if (localeForcedNavOrder.find(url => url === cur)) {
            return { ...acc, [cur]: [cur] };
          }

          let prefix;
          if (!isDefault && cur.startsWith(`/${localeSlug}/`)) {
            prefix = `/${localeSlug}/${cur.split('/')[2]}`;
          } else {
            prefix = `/${cur.split('/')[1]}`;
          }

          if (config.gatsby && config.gatsby.trailingSlash) {
            prefix = prefix + '/';
          }

          if (localeForcedNavOrder.find(url => url === prefix)) {
            return { ...acc, [prefix]: [...(acc[prefix] || []), cur] };
          } else {
            return { ...acc, items: [...acc.items, cur] };
          }
        },
        { items: [] }
      );

    const nav = localeForcedNavOrder
      .reduce((acc, cur) => {
        return acc.concat(navItems[cur]);
      }, [])
      .concat(navItems.items)
      .map(slug => {
        if (slug) {
          const found = allMdx.edges.find(({ node }) => node.fields.slug === slug);
          if (found) {
            return { title: found.node.fields.title, url: found.node.fields.slug };
          }
        }
      })
      .filter(Boolean);

    // meta tags
    const metaTitle = mdx.frontmatter.metaTitle;
    const metaDescription = mdx.frontmatter.metaDescription;

    let canonicalUrl = config.gatsby.siteUrl;
    canonicalUrl =
      config.gatsby.pathPrefix !== '/' ? canonicalUrl + config.gatsby.pathPrefix : canonicalUrl;
    // The docs home's slug is '/', and nginx 301s /docs/ to /docs: same rule as the hreflang below.
    canonicalUrl = canonicalUrl + (mdx.fields.slug === '/' ? '' : mdx.fields.slug);

    // Strip locale prefix to get content-only path for hreflang
    const stripLocalePrefix = (slug) => {
      if (!isDefault && localeSlug && slug.startsWith(`/${localeSlug}`)) {
        const stripped = slug.substring(localeSlug.length + 1);
        return stripped || '/';
      }
      return slug;
    };

    const contentSlug = stripLocalePrefix(mdx.fields.slug);
    const baseUrl = config.gatsby.siteUrl + (config.gatsby.pathPrefix !== '/' ? config.gatsby.pathPrefix : '');

    // SEO: home/section landings are websites, deeper docs are articles.
    const ogType = contentSlug === '/' ? 'website' : 'article';

    // Breadcrumb trail from the content slug (humanized intermediate segments).
    const segments = contentSlug.split('/').filter(Boolean);
    const crumbs = [{ name: 'Documentation', url: baseUrl }];
    segments.forEach((seg, i) => {
      const isLast = i === segments.length - 1;
      crumbs.push({
        name: isLast ? metaTitle : seg.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        url: `${baseUrl}/${segments.slice(0, i + 1).join('/')}`,
      });
    });
    const articleLd = {
      '@context': 'https://schema.org',
      '@type': 'TechArticle',
      headline: metaTitle,
      description: metaDescription,
      url: canonicalUrl,
      inLanguage: localeConfig.hreflang,
      isPartOf: { '@type': 'WebSite', name: 'DesignerBox Documentation', url: baseUrl },
      publisher: { '@type': 'Organization', name: 'DesignerBox', url: config.gatsby.siteUrl },
    };
    const breadcrumbLd = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: crumbs.map((c, i) => ({ '@type': 'ListItem', position: i + 1, name: c.name, item: c.url })),
    };

    // FAQPage schema on the pricing FAQ page, derived from its own "## question" / answer
    // body so it stays in sync with each locale's content automatically.
    let faqLd = null;
    if (contentSlug === '/pricing-faqs' && mdx.rawBody) {
      const heads = [...mdx.rawBody.matchAll(/^##\s+(.+?)\s*$/gm)];
      const faqs = heads
        .map((m, i) => {
          const q = m[1].trim();
          const start = m.index + m[0].length;
          const stop = i + 1 < heads.length ? heads[i + 1].index : mdx.rawBody.length;
          const a = mdx.rawBody
            .slice(start, stop)
            .replace(/!\[[^\]]*\]\([^)]*\)/g, '')   // images
            .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1') // links -> text
            .replace(/[*_`>#]/g, '')                 // markdown symbols
            .replace(/\s+/g, ' ')
            .trim();
          return { q, a };
        })
        .filter(f => f.q && f.a);
      if (faqs.length) {
        faqLd = {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map(f => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        };
      }
    }

    return (
      <LocaleProvider locale={locale}>
        <Layout {...this.props} locale={locale}>
          <Helmet htmlAttributes={{ lang: localeConfig.hreflang }}>
            {metaTitle ? <title>{metaTitle}</title> : null}
            {metaTitle ? <meta name="title" content={metaTitle} /> : null}
            {metaDescription ? <meta name="description" content={metaDescription} /> : null}
            <meta property="og:type" content={ogType} />
            <meta property="og:url" content={canonicalUrl} />
            {metaTitle ? <meta property="og:title" content={metaTitle} /> : null}
            {metaDescription ? <meta property="og:description" content={metaDescription} /> : null}
            {metaTitle ? <meta property="twitter:title" content={metaTitle} /> : null}
            {metaDescription ? (
              <meta property="twitter:description" content={metaDescription} />
            ) : null}
            <link rel="canonical" href={canonicalUrl} />
            {/* All locales are translated: emit per-locale hreflang alternates. */}
            {Object.entries(locales).map(([code, conf]) => {
              const altPrefix = conf.isDefault ? '' : `/${conf.slug}`;
              const altUrl = `${baseUrl}${altPrefix}${contentSlug === '/' ? '' : contentSlug}`;
              return <link key={code} rel="alternate" hreflang={conf.hreflang} href={altUrl} />;
            })}
            <link rel="alternate" hreflang="x-default" href={`${baseUrl}${contentSlug === '/' ? '' : contentSlug}`} />
            <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
            <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
            {faqLd ? (
              <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
            ) : null}
          </Helmet>
          <div className={'titleWrapper'}>
            <StyledHeading>{mdx.fields.title}</StyledHeading>
            <Edit className={'mobileView'}>
              {docsLocation && (
                <Link className={'gitBtn'} to={`${docsLocation}/${mdx.parent.relativePath}`}>
                  <img src={gitHub} alt={'Github logo'} /> Edit on GitHub
                </Link>
              )}
            </Edit>
          </div>
          <StyledMainWrapper>
            <MDXRenderer>{mdx.body}</MDXRenderer>
          </StyledMainWrapper>
          <div className={'addPaddTopBottom'}>
            <NextPrevious mdx={mdx} nav={nav} />
          </div>
        </Layout>
      </LocaleProvider>
    );
  }
}

export const pageQuery = graphql`
  query($id: String!, $locale: String!) {
    site {
      siteMetadata {
        title
        docsLocation
      }
    }
    mdx(fields: { id: { eq: $id } }) {
      fields {
        id
        title
        slug
        locale
      }
      body
      rawBody
      tableOfContents
      parent {
        ... on File {
          relativePath
        }
      }
      frontmatter {
        metaTitle
        metaDescription
        order
      }
    }
    allMdx(filter: { fields: { locale: { eq: $locale } } }) {
      edges {
        node {
          fields {
            slug
            title
          }
        }
      }
    }
  }
`;
